import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Plus, Ruler } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

const ProductCard = ({ product, onSizeGuideClick }) => {
  const { addToCart, isLoading } = useCart();
  const [selectedSize, setSelectedSize] = useState(product.sizes[0] || '');

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }
    await addToCart(product.id, selectedSize, 1);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="product-card group relative bg-charcoal border border-white/5 overflow-hidden z-10"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-obsidian">
        <img
          src={product.image}
          alt={product.name}
          className="product-image w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Quick Add Button */}
        <Button
          onClick={handleAddToCart}
          disabled={isLoading}
          className="absolute bottom-4 left-4 right-4 bg-gold-500 text-black hover:bg-gold-400 rounded-none py-3 text-xs uppercase tracking-widest font-bold opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300 z-20"
          data-testid={`add-to-cart-${product.id}`}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add to Cart
        </Button>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold-500 font-sans mb-2">{product.category}</p>
          <h3 className="font-serif text-xl text-white group-hover:text-gold-400 transition-colors">
            {product.name}
          </h3>
          <p className="text-sm text-white/50 mt-2 line-clamp-2">{product.description}</p>
        </div>

        {/* Specs Preview */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(product.specs).slice(0, 2).map(([key, value]) => (
            <span key={key} className="text-[10px] uppercase tracking-wider text-white/40 bg-white/5 px-2 py-1">
              {value}
            </span>
          ))}
        </div>

        {/* Size Selector & Price */}
        <div className="flex items-center justify-between pt-4 border-t border-white/5 relative z-20">
          <div className="flex items-center gap-2">
            <Select value={selectedSize} onValueChange={setSelectedSize}>
              <SelectTrigger className="w-24 h-9 bg-white/5 border-white/10 text-white text-xs rounded-none" data-testid={`size-select-${product.id}`}>
                <SelectValue placeholder="Size" />
              </SelectTrigger>
              <SelectContent className="bg-charcoal border-white/10 z-50">
                {product.sizes.map((size) => (
                  <SelectItem key={size} value={size} className="text-white hover:bg-white/5">
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              className="w-9 h-9 text-white/40 hover:text-gold-500 hover:bg-transparent"
              onClick={(e) => { e.stopPropagation(); onSizeGuideClick(); }}
              data-testid={`size-guide-btn-${product.id}`}
            >
              <Ruler className="w-4 h-4" />
            </Button>
          </div>
          <p className="font-serif text-xl italic text-gold-400" data-testid={`product-price-${product.id}`}>
            ${product.price.toLocaleString()}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const ProductCarousel = ({ products, onSizeGuideClick }) => {
  return (
    <section id="collections" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-obsidian" data-testid="collections-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 space-y-4"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-gold-500 font-sans">The Collection</p>
          <h2 className="text-4xl md:text-5xl font-serif text-white">
            Exceptional <span className="italic text-gold-400">Timepieces</span>
          </h2>
          <p className="text-white/50 max-w-xl mx-auto">
            Each watch in our collection represents the pinnacle of horological achievement, crafted with unwavering dedication to excellence.
          </p>
        </motion.div>

        {/* Carousel */}
        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {products.map((product) => (
              <CarouselItem key={product.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <ProductCard product={product} onSizeGuideClick={onSizeGuideClick} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex -left-12 bg-charcoal border-white/10 text-white hover:bg-gold-500 hover:text-black hover:border-gold-500" data-testid="carousel-prev" />
          <CarouselNext className="hidden md:flex -right-12 bg-charcoal border-white/10 text-white hover:bg-gold-500 hover:text-black hover:border-gold-500" data-testid="carousel-next" />
        </Carousel>

        {/* Mobile Navigation Hint */}
        <p className="text-center text-white/30 text-sm mt-8 md:hidden">
          Swipe to explore more
        </p>
      </div>
    </section>
  );
};

export default ProductCarousel;
